'use client'

import React, { useState, useEffect, useRef, useCallback } from 'react'
import { motion } from 'framer-motion'
import { usePathname } from 'next/navigation'
import styles from './NotFoundHero.module.scss'
import { usePreloader } from '@/components/PagePreloader/PreloaderContext'

// Vimeo Player API types
interface VimeoPlayer {
  on: (event: string, callback: () => void) => void
  off: (event: string, callback: () => void) => void
  destroy: () => void
  ready: () => Promise<void>
  play: () => Promise<void>
}

export const NotFoundHero: React.FC = () => {
  const [showText, setShowText] = useState(false)
  const pathname = usePathname()
  const isAdminMode = pathname.startsWith('/admin')
  const { state: preloaderState, isInitialLoad } = usePreloader()

  // Hardcoded Vimeo ID and hash as specified
  const vimeoId = '1108928059'
  const hashParam = '940b09c208'

  // Vimeo player refs
  const playerRef = useRef<VimeoPlayer | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Video loading states
  const [isVimeoAPILoaded, setIsVimeoAPILoaded] = useState(false)
  const [videoLoaded, setVideoLoaded] = useState(false)
  const [videoPlaying, setVideoPlaying] = useState(false)

  // Check if video should be visible (loaded, playing)
  const shouldShowVideo = isAdminMode || (videoLoaded && videoPlaying)

  // Load Vimeo Player API
  const loadVimeoAPI = useCallback(() => {
    if ((window as unknown as { Vimeo?: unknown }).Vimeo || isVimeoAPILoaded) {
      setIsVimeoAPILoaded(true)
      return Promise.resolve()
    }

    return new Promise<void>((resolve) => {
      const script = document.createElement('script')
      script.src = 'https://player.vimeo.com/api/player.js'
      script.onload = () => {
        setIsVimeoAPILoaded(true)
        resolve()
      }
      document.head.appendChild(script)
    })
  }, [isVimeoAPILoaded])

  // Initialize Vimeo Player
  const initializeVimeoPlayer = useCallback(async () => {
    if (!containerRef.current || !isVimeoAPILoaded || playerRef.current || !vimeoId) {
      return
    }

    try {
      await loadVimeoAPI()
      
      // Build player options for unlisted video with hash parameter
      const playerOptions: Record<string, unknown> = {
        url: `https://player.vimeo.com/video/${vimeoId}?h=${hashParam}&dnt=1`,
        responsive: false,
        width: 1920,
        height: 1080,
        background: true,
        autoplay: true,
        loop: true,
        muted: true,
        controls: false,
        byline: false,
        title: false,
        portrait: false
      }
      
      const player = new (window as unknown as { Vimeo: { Player: new (el: HTMLElement, opts: unknown) => VimeoPlayer } }).Vimeo.Player(containerRef.current, playerOptions) as VimeoPlayer

      playerRef.current = player

      // Listen for video events
      player.on('loaded', () => {
        setVideoLoaded(true)
      })
      player.on('play', () => {
        setVideoPlaying(true)
      })

      // Wait for player to be ready
      await player.ready()
      
      // Manually trigger play for unlisted videos (autoplay might be blocked)
      try {
        await player.play()
      } catch {
        // Still call onPlaying to show the video even if autoplay fails
        setVideoPlaying(true)
      }
      
    } catch {
      // Fallback: show video after a delay
      setTimeout(() => {
        setVideoLoaded(true)
        setVideoPlaying(true)
      }, 2000)
    }
  }, [isVimeoAPILoaded, loadVimeoAPI, vimeoId, hashParam])

  // Load Vimeo API when component mounts
  useEffect(() => {
    loadVimeoAPI()
  }, [loadVimeoAPI])

  // Initialize Vimeo player when API is loaded
  useEffect(() => {
    if (isVimeoAPILoaded && vimeoId) {
      initializeVimeoPlayer()
    }
  }, [isVimeoAPILoaded, initializeVimeoPlayer])

  // Cleanup Vimeo player on unmount
  useEffect(() => {
    return () => {
      if (playerRef.current) {
        try {
          playerRef.current.destroy()
        } catch {
          // Ignore destroy errors
        }
        playerRef.current = null
      }
    }
  }, [])

  // Animation trigger: only after preloader is completely finished
  useEffect(() => {
    if (isAdminMode) {
      setShowText(true) // Show immediately in admin
      return
    }

    // Only trigger when preloader state is 'hidden' and we're not on initial load
    if (preloaderState === 'hidden' && !isInitialLoad) {
      setShowText(true)
    }
  }, [isAdminMode, preloaderState, isInitialLoad])

  return (
    <section className={styles.hero}>
      {/* Vimeo video background */}
      <motion.div 
        ref={containerRef}
        className={styles['hero__vimeo-wrapper']}
        initial={{ opacity: isAdminMode ? 1 : 0 }}
        animate={{ opacity: isAdminMode ? 1 : (shouldShowVideo ? 1 : 0) }}
        transition={{ 
          duration: 1.0,
          ease: [0.25, 0.46, 0.45, 0.94]
        }}
        title="404 Hero Video"
      />

      <div className={styles.hero__overlay}></div>
      
      <div className={styles.hero__container}>
        <div className={styles.hero__content}>
          <motion.div 
            className={styles.hero__text}
            initial={{ opacity: isAdminMode ? 1 : 0 }}
            animate={{ 
              opacity: showText ? 1 : 0 
            }}
            transition={{ 
              duration: 0.8,
              delay: isAdminMode ? 0 : 0.1,
              ease: [0.25, 0.46, 0.45, 0.94]
            }}
          >
            <h1 className={styles.hero__number}>404</h1>
            <p className={styles.hero__message}>page not found</p>
          </motion.div>
        </div>
      </div>
    </section>
  )
}